import { LoadingBlock } from "../../UI/Loading/LoadingRows.tsx";
import { ReactNode, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Card } from "../../UI/Card.tsx";
import { SegmentedControl } from "../../UI/SegmentedControl.tsx";
import { SummaryStat } from "../../UI/Chart/SummaryStat.tsx";
import {
  ACTIVITY_PERIOD_WEEK_OPTIONS,
  ActivityPeriodWeeks,
  DEFAULT_ACTIVITY_PERIOD_WEEKS,
  lastCompleteWeeks,
} from "./activityPeriod.ts";
import { useTeacherActivity } from "./useTeacherActivity.ts";
import {
  NEW_TEACHER_BOOST_MAX_CLASSES,
  NEW_TEACHER_BOOST_WEEKS,
  NewTeacherBoost,
  useNewTeacherBoost,
} from "./useNewTeacherBoost.ts";
import {
  formatDateRange,
  formatHours,
  formatOneDecimal,
  formatShare,
} from "./activityFormat.ts";
import { ClassRoomKind, GroupClassFill } from "./workloadStats.ts";
import { WeeklyAvailabilityChart } from "./WeeklyAvailabilityChart.tsx";
import { AvailabilityHeatmap } from "./AvailabilityHeatmap.tsx";

const PERIOD_OPTIONS = ACTIVITY_PERIOD_WEEK_OPTIONS.map((weeks) => ({
  value: weeks,
  label: `${weeks} weeks`,
}));

const CLASS_ROOM_KIND_LABELS: Record<ClassRoomKind, string> = {
  group: "group",
  oneOnOne: "1to1",
  progressCheck: "progress check",
};

function describeClassRoomKinds(
  classRoomsByKind: Record<ClassRoomKind, number>,
): string {
  const kindsWithClassRooms = (
    Object.entries(classRoomsByKind) as [ClassRoomKind, number][]
  ).filter(([, count]) => count > 0);
  if (kindsWithClassRooms.length === 0) return "No finished classes";
  return kindsWithClassRooms
    .map(([kind, count]) => `${count} ${CLASS_ROOM_KIND_LABELS[kind]}`)
    .join(" · ");
}

function describeGroupClassFill({
  averageStudents,
  averageSeats,
}: GroupClassFill): string {
  if (averageStudents === null || averageSeats === null)
    return "No group classes";
  return `${formatOneDecimal(averageStudents)} of ${formatOneDecimal(averageSeats)} seats on average`;
}

function StatDetail({ children }: { children: ReactNode }) {
  return <span className={"text-xs text-slate-400"}>{children}</span>;
}

function NewTeacherBoostBadge({ boost }: { boost: NewTeacherBoost }) {
  if (!boost.isActive) return null;
  return (
    <span
      title={`New teachers get priority for their first ${NEW_TEACHER_BOOST_WEEKS} weeks, as long as they taught fewer than ${NEW_TEACHER_BOOST_MAX_CLASSES} classes`}
      className={
        "mt-1 inline-flex w-fit items-center rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700"
      }
    >
      New teacher priority · until {boost.until.format("D MMM")}
    </span>
  );
}

export function TeacherActivityCard({
  teacherId,
  isInternalTeacher,
}: {
  teacherId: string;
  isInternalTeacher: boolean;
}) {
  const now = useMemo(() => dayjs(), []);
  const [periodWeeks, setPeriodWeeks] = useState<ActivityPeriodWeeks>(
    DEFAULT_ACTIVITY_PERIOD_WEEKS,
  );
  const period = useMemo(
    () => lastCompleteWeeks(periodWeeks, now),
    [periodWeeks, now],
  );
  const { availability, assignments, teaching } = useTeacherActivity({
    teacherId,
    period,
    now,
  });
  const newTeacherBoost = useNewTeacherBoost(teacherId);

  const { summary } = availability;
  const { outcome } = assignments;
  const { feedback, workload } = teaching;

  return (
    <Card>
      <div className={"flex flex-wrap items-start justify-between gap-3"}>
        <div className={"flex flex-col gap-1"}>
          <h2
            className={
              "text-sm font-semibold text-slate-500 uppercase tracking-wide"
            }
          >
            Activity
          </h2>
          <span className={"text-xs text-slate-400"}>
            {formatDateRange(period.from, period.to)} · last {periodWeeks}{" "}
            complete weeks
          </span>
        </div>
        <SegmentedControl
          options={PERIOD_OPTIONS}
          value={periodWeeks}
          onChange={setPeriodWeeks}
        />
      </div>

      <div className={"flex flex-col gap-8"}>
        <div className={"grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6"}>
          <SummaryStat
            label={"Classes taught"}
            loading={teaching.loading}
            value={String(workload.taughtClassRooms)}
          >
            {!teaching.loading && (
              <StatDetail>
                {describeClassRoomKinds(workload.taughtClassRoomsByKind)}
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Students taught"}
            loading={teaching.loading}
            value={String(workload.distinctStudents)}
          >
            {!teaching.loading && (
              <StatDetail>Each student counted once</StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Group class fill"}
            loading={teaching.loading}
            value={formatShare(workload.groupClassFill.fillRate)}
          >
            {!teaching.loading && (
              <StatDetail>
                {describeGroupClassFill(workload.groupClassFill)}
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Student feedback written"}
            loading={teaching.loading}
            value={formatShare(feedback.completionRate)}
          >
            {!teaching.loading && (
              <StatDetail>
                {feedback.filledFeedbacks} of {feedback.expectedFeedbacks}{" "}
                students
                {feedback.classRoomsMissingFeedback > 0 &&
                  ` · ${feedback.classRoomsMissingFeedback} classes incomplete`}
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Availability per week"}
            loading={availability.loading}
            value={formatHours(summary.averageOfferedHoursPerWeek)}
          >
            {!availability.loading && (
              <StatDetail>
                {formatHours(summary.offeredHours)} offered in {periodWeeks}{" "}
                weeks
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Availability booked"}
            loading={availability.loading}
            value={formatShare(summary.bookedShare)}
          >
            {!availability.loading && (
              <StatDetail>
                {formatHours(summary.bookedHours)} of{" "}
                {formatHours(summary.offeredHours)}
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Picked by the assignment algorithm"}
            loading={assignments.loading}
            value={formatShare(outcome.winRate)}
          >
            {!assignments.loading && (
              <StatDetail>
                {outcome.wonClassRooms} won · {outcome.lostClassRooms} lost to
                other teachers
              </StatDetail>
            )}
            <NewTeacherBoostBadge boost={newTeacherBoost} />
          </SummaryStat>
        </div>

        {availability.loading ? (
          <LoadingBlock sizeClassName={"h-56 w-full"} />
        ) : (
          <>
            <WeeklyAvailabilityChart weeks={availability.weeks} />
            <AvailabilityHeatmap
              cells={availability.byWeekdayAndHour}
              weeksInPeriod={periodWeeks}
            />
          </>
        )}

        <div className={"flex flex-col gap-1.5 text-xs text-slate-400"}>
          <p>
            Availability comes from ShiftBase: booked hours are the classes
            assigned to the teacher, free hours are open slots nobody booked.
            Weeks run Sunday to Saturday, as in the teacher area, and the
            current week is left out.
          </p>
          <p>
            The assignment algorithm figure compares the classes the teacher got
            with the classes where the algorithm considered them as a candidate
            but picked another teacher. Assignment logs used to be deleted after
            90 days, so older assignments may be missing.
            {!isInternalTeacher &&
              " When an internal teacher is free, only internal teachers are logged as candidates, so classes lost to an internal teacher are not counted."}
          </p>
          <p>
            Classes taught are the finished classes with at least one student.
            Group class fill compares the students still enrolled when the class
            took place with its seats. Student feedback counts the students of
            those classes who got a lesson feedback, or a progress check result,
            from the teacher.
          </p>
        </div>
      </div>
    </Card>
  );
}
